import { playerHitsObstacle, removeConesToppledByCarts } from './collision';
import { decideNextObstacle, type LastObstacleType } from './spawn-rules';
import {
  GameConfig,
  type GameState,
  type Obstacle,
  type Player,
} from './game-types';

export class GameEngine {
  state: GameState = 'idle';
  player: Player;
  obstacles: Obstacle[] = [];
  score = 0;
  highScore = 0;
  gameSpeed = GameConfig.INITIAL_SPEED;
  floorOffset = 0;
  lastFrameMovement = 0;
  private frameCount = 0;
  private distanceSinceLastObstacle = 0;
  private lastObstacleType: LastObstacleType = null;
  private lastCartAt = 0;

  constructor() {
    this.player = this.createPlayer();
    this.highScore = this.loadHighScore();
  }

  private createPlayer(): Player {
    return {
      x: GameConfig.PLAYER_X,
      y: GameConfig.GROUND_Y - GameConfig.PLAYER_SIZE,
      width: GameConfig.PLAYER_SIZE,
      height: GameConfig.PLAYER_SIZE,
      velocityY: 0,
      isJumping: false,
      isDucking: false,
    };
  }

  start() {
    this.player = this.createPlayer();
    this.obstacles = [];
    this.score = 0;
    this.gameSpeed = GameConfig.INITIAL_SPEED;
    this.frameCount = 0;
    this.distanceSinceLastObstacle = 0;
    this.lastObstacleType = null;
    this.lastCartAt = 0;
    this.floorOffset = 0;
    this.highScore = this.loadHighScore();
    this.state = 'running';
  }

  jump() {
    if (this.state === 'idle' || this.state === 'game-over') {
      this.start();
      return;
    }

    if (this.state === 'running' && !this.player.isJumping) {
      if (this.player.isDucking) {
        this.duck(false);
      }
      this.player.velocityY = GameConfig.JUMP_FORCE;
      this.player.isJumping = true;
    }
  }

  duck(active: boolean) {
    if (this.state !== 'running') return;
    if (this.player.isJumping && active) return;

    this.player.isDucking = active;
    if (active) {
      this.player.height = GameConfig.DUCK_HEIGHT;
      this.player.y = GameConfig.GROUND_Y - GameConfig.DUCK_HEIGHT;
    } else {
      this.player.height = GameConfig.PLAYER_SIZE;
      this.player.y = GameConfig.GROUND_Y - GameConfig.PLAYER_SIZE;
    }
  }

  update(dt: number) {
    if (this.state !== 'running') return;

    this.player.velocityY += GameConfig.GRAVITY * dt;
    this.player.y += this.player.velocityY * dt;

    const groundLevel = GameConfig.GROUND_Y - this.player.height;
    if (this.player.y >= groundLevel) {
      this.player.y = groundLevel;
      this.player.velocityY = 0;
      this.player.isJumping = false;
    }

    if (this.gameSpeed < GameConfig.MAX_SPEED) {
      this.gameSpeed += GameConfig.SPEED_INCREMENT * dt;
    }

    this.lastFrameMovement = this.gameSpeed * dt;
    this.floorOffset = (this.floorOffset + this.gameSpeed * dt) % 20;

    for (let i = this.obstacles.length - 1; i >= 0; i--) {
      this.obstacles[i].x -= this.gameSpeed * dt * (this.obstacles[i].speedMul ?? 1);
      if (this.obstacles[i].x < -this.obstacles[i].width) {
        this.obstacles.splice(i, 1);
      }
    }

    this.obstacles = removeConesToppledByCarts(this.obstacles);

    this.distanceSinceLastObstacle += this.gameSpeed * dt;
    const gap = GameConfig.MIN_OBSTACLE_GAP + Math.random() * 150;
    if (this.distanceSinceLastObstacle > gap) {
      this.trySpawn();
      this.distanceSinceLastObstacle = 0;
    }

    for (const obstacle of this.obstacles) {
      if (playerHitsObstacle(this.player, obstacle)) {
        this.state = 'game-over';
        if (this.score > this.highScore) {
          this.highScore = Math.floor(this.score);
          this.saveHighScore();
        }
        return;
      }
    }

    this.score +=
      GameConfig.SCORE_PER_FRAME * (this.gameSpeed / GameConfig.INITIAL_SPEED) * dt;
    this.frameCount++;
  }

  private trySpawn() {
    const decision = decideNextObstacle({
      obstacles: this.obstacles,
      lastObstacleType: this.lastObstacleType,
      lastCartAt: this.lastCartAt,
      now: performance.now(),
    });
    if (decision.obstacle) {
      this.obstacles.push(decision.obstacle);
    }
    this.lastObstacleType = decision.lastObstacleType;
    this.lastCartAt = decision.lastCartAt;
  }

  private loadHighScore(): number {
    try {
      const stored = localStorage.getItem(GameConfig.HIGH_SCORE_KEY);
      return stored ? parseInt(stored, 10) || 0 : 0;
    } catch {
      return 0;
    }
  }

  private saveHighScore() {
    try {
      localStorage.setItem(GameConfig.HIGH_SCORE_KEY, String(this.highScore));
    } catch {
      // localStorage may be unavailable
    }
  }
}
